/**
 * useGapAnalysisData Hook
 * Fetches and manages gap analysis data from the API
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../lib/apiClient';

/**
 * Custom hook for gap analysis data
 * @param {Object} options - Hook options
 * @param {number[]} options.departmentIds - Optional filter by department IDs
 * @param {string} options.startDate - Optional start date filter
 * @param {string} options.endDate - Optional end date filter
 * @returns {Object} Gap analysis data and methods
 */
const useGapAnalysisData = (options = {}) => {
  const { departmentIds, startDate, endDate } = options;
  // Serialize departmentIds to a stable string for dependency tracking
  const deptIdsKey = departmentIds ? departmentIds.join(',') : '';

  // State
  const [coverage, setCoverage] = useState(null);
  const [coverageStats, setCoverageStats] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [gapStats, setGapStats] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs for abort controllers
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  /**
   * Fetch all gap analysis data
   */
  const fetchData = useCallback(async () => {
    // Cancel any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel — use allSettled so partial failures
      // don't block the entire page from rendering
      // Pass departmentId filter to all endpoints that support it
      const deptId = departmentIds?.[0] || undefined;
      const deptQuery = deptId ? { departmentId: deptId } : {};

      const results = await Promise.allSettled([
        apiClient.get('/api/gap-analysis/coverage', { params: deptQuery, signal: controller.signal }),
        apiClient.get('/api/gap-analysis/coverage/stats', { signal: controller.signal }),
        apiClient.get('/api/gap-analysis/gaps', { params: deptQuery, signal: controller.signal }),
        apiClient.get('/api/gap-analysis/gaps/stats', { signal: controller.signal }),
        apiClient.get('/api/gap-analysis/suggestions', { params: { maxCount: 10, ...deptQuery }, signal: controller.signal }),
        apiClient.get('/api/gap-analysis/duplicates', { params: deptQuery, signal: controller.signal }),
        apiClient.get('/api/gap-analysis/timeline', {
          params: { departmentIds: departmentIds?.join(','), startDate, endDate },
          signal: controller.signal
        })
      ]);

      const val = (i) => results[i].status === 'fulfilled' ? results[i].value : null;

      if (isMountedRef.current && !controller.signal.aborted) {
        setCoverage(val(0));
        setCoverageStats(val(1));
        setGaps(val(2));
        setGapStats(val(3));
        setSuggestions(val(4));
        setDuplicates(val(5));
        setTimeline(val(6));

        // Only set error if ALL requests failed
        const allFailed = results.every(r => r.status === 'rejected');
        if (allFailed) {
          setError(results[0].reason);
        } else {
          setError(null);
        }
      }
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return;
      }

      console.error('[useGapAnalysisData] Error fetching data:', err);

      if (isMountedRef.current && !controller.signal.aborted) {
        setError(err);
      }
    } finally {
      if (isMountedRef.current && !controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [deptIdsKey, startDate, endDate]); // deptIdsKey is a serialized string of departmentIds

  /**
   * Refetch all data
   */
  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Trigger data refresh on the backend (materialzied view + gap detection)
   */
  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      await apiClient.post('/api/gap-analysis/refresh');
      // Refetch after refresh
      await fetchData();
    } catch (err) {
      console.error('[useGapAnalysisData] Error refreshing data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  /**
   * Detect gaps for a specific department
   */
  const detectGaps = useCallback(async (departmentId = null, lookbackMonths = 24) => {
    try {
      const result = await apiClient.post('/api/gap-analysis/detect-gaps', {
        departmentId,
        lookbackMonths
      });
      return result;
    } catch (err) {
      console.error('[useGapAnalysisData] Error detecting gaps:', err);
      throw err;
    }
  }, []);

  /**
   * Resolve a gap
   */
  const resolveGap = useCallback(async (gapId, notes = null) => {
    try {
      const result = await apiClient.put(`/api/gap-analysis/gaps/${gapId}/resolve`, {
        notes
      });

      // Update local state
      if (isMountedRef.current) {
        setGaps(prev => prev?.map(g =>
          g.gap_id === gapId ? { ...g, is_resolved: true } : g
        ));
      }

      return result;
    } catch (err) {
      console.error('[useGapAnalysisData] Error resolving gap:', err);
      throw err;
    }
  }, []);

  /**
   * Log a scrape coverage event
   */
  const logScrapeCoverage = useCallback(async (data) => {
    try {
      const result = await apiClient.post('/api/gap-analysis/scrape-log', data);
      return result;
    } catch (err) {
      console.error('[useGapAnalysisData] Error logging scrape coverage:', err);
      throw err;
    }
  }, []);

  /**
   * Get scrape history for a department
   */
  const getScrapeHistory = useCallback(async (departmentId = null, limit = 50) => {
    try {
      const result = await apiClient.get('/api/gap-analysis/scrape-history', {
        params: { departmentId, limit }
      });
      return result;
    } catch (err) {
      console.error('[useGapAnalysisData] Error fetching scrape history:', err);
      throw err;
    }
  }, []);

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    isMountedRef.current = true;
    fetchData();

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  return {
    // Data
    coverage,
    coverageStats,
    gaps,
    gapStats,
    suggestions,
    duplicates,
    timeline,

    // State
    loading,
    error,

    // Methods
    refetch,
    refreshData,
    detectGaps,
    resolveGap,
    logScrapeCoverage,
    getScrapeHistory
  };
};

export default useGapAnalysisData;
